import { create } from 'zustand'
import { workflowsApi, type PendingApproval } from '@/lib/api'
import { toast } from 'sonner'

interface WorkflowApprovalsStore {
  pending: PendingApproval[]
  loading: boolean

  fetchPending: () => Promise<void>
  approve: (workflowId: number) => Promise<void>
  // The reason is required: the Rep sees it on the rejected workflow.
  reject: (workflowId: number, reason: string) => Promise<void>
  reset: () => void
}

const initialState = {
  pending: [],
  loading: false,
}

export const useWorkflowApprovalsStore = create<WorkflowApprovalsStore>((set, get) => ({
  ...initialState,

  fetchPending: async () => {
    set({ loading: true })
    try {
      const response = await workflowsApi.pendingApprovals()
      set({ pending: response.data, loading: false })
    } catch {
      // Expected for any role other than Owner/Admin/Manager (403) -- see
      // WorkflowApprovalsSection, which never calls this without checking
      // usePermission first, so a failure here is unusual, not routine.
      set({ loading: false })
    }
  },

  approve: async (workflowId: number) => {
    try {
      await workflowsApi.approveActivation(workflowId)
      set({ pending: get().pending.filter((p) => p.workflow_id !== workflowId) })
      toast.success('Workflow activated')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve activation')
      throw error
    }
  },

  reject: async (workflowId: number, reason: string) => {
    try {
      await workflowsApi.rejectActivation(workflowId, reason)
      set({ pending: get().pending.filter((p) => p.workflow_id !== workflowId) })
      toast.success('Activation request rejected')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject activation')
      throw error
    }
  },

  reset: () => set(initialState),
}))
